import React, { useEffect, useState } from "react";
import { ChevronLeft, Save, Languages, Eye, Trash2, ImageIcon, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ImageCropUpload } from "@/components/ImageCropUpload";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { ResourceHistory, Actor } from "@/components/admin/ResourceHistory";

const LANGS = [["fr", "Français"], ["en", "English"], ["es", "Español"], ["pt", "Português"]];
const TARGETS = ["en", "es", "pt"];
const MUTED = { color: "var(--yalola-text-muted)" };
const blank = (category) => ({ id: null, slug: "", status: "draft", published_at: null, category, cover_url: "", author_label: "L'équipe Yalola",
  i18n: { fr: {}, en: {}, es: {}, pt: {} } });
const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); if (Number.isNaN(d.getTime())) return ""; const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

const Field = ({ label, hint, children, testId }) => (
  <label className="block space-y-1" data-testid={testId}>
    <span className="text-xs font-semibold" style={MUTED}>{label}</span>
    {children}
    {hint && <span className="block text-[11px]" style={MUTED}>{hint}</span>}
  </label>
);

// Édition d'un article : 4 langues côte à côte (FR obligatoire), éditeur riche, couverture, statut / programmation, traduction IA, aperçu.
export const ResourceEditor = ({ articleId, categories, onBack, onSaved }) => {
  const [art, setArt] = useState(articleId ? null : blank(categories[0]?.key || ""));
  const [lang, setLang] = useState("fr");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (!articleId) return;
    api.get(`/admin/resources/${articleId}`).then(({ data }) => setArt(data)).catch(() => { toast.error("Article introuvable"); onBack(); });
  }, [articleId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!art) return <div className="yalola-card h-64 animate-pulse" data-testid="res-editor-loading" />;
  const cur = art.i18n[lang] || {};
  const setField = (k, v) => { setArt((a) => ({ ...a, [k]: v })); setDirty(true); };
  const setLangField = (k, v) => { setArt((a) => ({ ...a, i18n: { ...a.i18n, [lang]: { ...(a.i18n[lang] || {}), [k]: v, ai_translated: k === "body_html" || k === "title" ? false : (a.i18n[lang] || {}).ai_translated } } })); setDirty(true); };
  const frTitle = (art.i18n.fr || {}).title || "";

  const save = async (extra = {}) => {
    if (!frTitle.trim()) { setLang("fr"); return toast.error("Le titre en français est obligatoire."); }
    setBusy("save");
    try {
      const payload = { i18n: art.i18n, category: art.category, cover_url: art.cover_url || "", author_label: art.author_label || "", status: art.status,
        published_at: art.published_at || null, slug: art.slug || frTitle, ...extra };
      const { data } = art.id ? await api.patch(`/admin/resources/${art.id}`, payload) : await api.post("/admin/resources", payload);
      setArt(data); setDirty(false); onSaved?.();
      toast.success(data.status === "published" ? "Article enregistré (publié)" : "Brouillon enregistré");
      return data;
    } catch (e) { toast.error(e?.friendlyMessage || "Enregistrement impossible"); return null; }
    finally { setBusy(""); }
  };

  const translate = async () => {
    let current = art;
    if (dirty || !art.id) { current = await save(); if (!current) return; }
    setBusy("translate");
    try {
      const { data } = await api.post(`/admin/resources/${current.id}/translate`, { targets: TARGETS, overwrite });
      setArt(data.article); setEditorKey((k) => k + 1);
      const errs = Object.keys(data.errors || {});
      if (data.translated.length) toast.success(`Traduit avec l'IA : ${data.translated.map((l) => l.toUpperCase()).join(", ")} — relis avant de publier.`);
      if (data.skipped.length && !data.translated.length) toast.info(`Déjà traduit : ${data.skipped.map((l) => l.toUpperCase()).join(", ")} (coche « Écraser » pour retraduire).`);
      if (errs.length) toast.error(`Échec : ${errs.map((l) => l.toUpperCase()).join(", ")}`);
    } catch (e) { toast.error(e?.friendlyMessage || "Traduction impossible"); }
    finally { setBusy(""); }
  };

  const aiCover = async () => {
    let current = art;
    if (dirty || !art.id) { current = await save(); if (!current) return; }
    setBusy("cover");
    try {
      const { data } = await api.post(`/admin/resources/${current.id}/ai-cover`, {});
      setArt(data.article); toast.success("Couverture générée par l'IA");
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Génération impossible"); }
    finally { setBusy(""); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/resources/${art.id}`); toast.success("Article supprimé"); onBack(); }
    catch (e) { toast.error(e?.friendlyMessage || "Suppression impossible"); }
  };

  return (
    <div className="space-y-3" data-testid="res-editor">
      <div className="yalola-card p-3 flex items-center gap-3">
        <Button size="sm" variant="ghost" onClick={onBack} data-testid="res-back"><ChevronLeft size={16} />Retour</Button>
        <div className="min-w-0 flex-1">
          <div className="font-bold font-display truncate" data-testid="res-editor-title">{frTitle || "Nouvel article"}</div>
          <div className="text-xs flex flex-wrap items-center gap-x-2" style={MUTED}>
            <span>{art.id ? `/ressources/${art.slug}` : "Brouillon non enregistré"}</span>
            {art.ai_generated && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "#EDE9FE", color: "#6D28D9" }} data-testid="res-ai-badge"><Sparkles size={10} />Rédigé par l'IA</span>}
            {art.status === "published" && art.published_by && <span data-testid="res-published-by">· Validé et publié par <Actor by={art.published_by} /> le {new Date(art.published_by_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
            {dirty && <span className="font-semibold" style={{ color: "#B45309" }} data-testid="res-dirty">· modifications non enregistrées</span>}
          </div>
        </div>
        <Button size="sm" onClick={() => save()} disabled={!!busy} data-testid="res-save">{busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}Enregistrer</Button>
      </div>

      <div className="grid md:grid-cols-[minmax(0,1fr)_300px] gap-3 items-start">
        <div className="yalola-card p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-1.5" role="tablist" data-testid="res-lang-tabs">
            {LANGS.map(([code, label]) => {
              const has = !!(art.i18n[code] || {}).title;
              const ai = !!(art.i18n[code] || {}).ai_translated;
              return (
                <button key={code} type="button" role="tab" aria-selected={lang === code} onClick={() => setLang(code)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${lang === code ? "text-white" : "hover:bg-black/5"}`}
                  style={lang === code ? { background: "var(--yalola-primary)", borderColor: "var(--yalola-primary)" } : { borderColor: "var(--yalola-border)" }}
                  data-testid={`res-lang-${code}`} data-has={has ? "1" : "0"} data-ai={ai ? "1" : "0"}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: has ? "#15803D" : "#D1D5DB" }} />{label}{code === "fr" && " *"}{ai && <span className="rounded px-1 text-[10px]" style={{ background: "rgba(255,255,255,.25)" }}>IA</span>}
                </button>
              );
            })}
          </div>
          {cur.ai_translated && <p className="text-xs rounded-lg px-3 py-2" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="res-ai-hint">Traduction générée par l'IA : relis et corrige si besoin avant de publier.</p>}
          <Field label={`Titre (${lang.toUpperCase()})${lang === "fr" ? " — obligatoire" : ""}`}>
            <Input value={cur.title || ""} onChange={(e) => setLangField("title", e.target.value)} maxLength={200} placeholder="Titre de l'article" data-testid="res-title" />
          </Field>
          <Field label="Extrait (chapeau affiché dans la liste et le partage)">
            <Textarea value={cur.excerpt || ""} onChange={(e) => setLangField("excerpt", e.target.value)} maxLength={600} rows={2} placeholder="Deux ou trois phrases qui résument l'article" data-testid="res-excerpt" />
          </Field>
          <div className="space-y-1">
            <span className="text-xs font-semibold" style={MUTED}>Corps de l'article</span>
            <RichTextEditor key={`${lang}-${editorKey}`} value={cur.body_html || ""} onChange={(html) => setLangField("body_html", html)} testId="res-body" />
          </div>
          <Field label={`Méta-description SEO (${(cur.seo_description || "").length}/160 conseillé)`} hint="Vide → l'extrait est utilisé.">
            <Input value={cur.seo_description || ""} onChange={(e) => setLangField("seo_description", e.target.value)} maxLength={320} data-testid="res-seo" />
          </Field>
        </div>

        <aside className="space-y-3">
          <div className="yalola-card p-4 space-y-3">
            <Field label="Statut">
              <select value={art.status} onChange={(e) => setField("status", e.target.value)} className="h-10 w-full rounded-lg border bg-white px-3 text-sm" data-testid="res-status">
                <option value="draft">Brouillon (invisible)</option>
                <option value="published">Publié</option>
              </select>
            </Field>
            {art.ai_generated && art.status !== "published" && <p className="text-[11px] rounded-lg px-2.5 py-1.5" style={{ background: "#EDE9FE", color: "#6D28D9" }} data-testid="res-ai-publish-hint">Brouillon IA : relis et corrige avant de passer en « Publié ». Ta validation sera enregistrée dans l'historique.</p>}
            <Field label="Date de publication" hint="Vide = maintenant à la publication. Une date future programme l'article.">
              <Input type="datetime-local" value={toLocalInput(art.published_at)} onChange={(e) => setField("published_at", fromLocalInput(e.target.value))} data-testid="res-published-at" />
            </Field>
            <Field label="Catégorie">
              <select value={art.category || ""} onChange={(e) => setField("category", e.target.value)} className="h-10 w-full rounded-lg border bg-white px-3 text-sm" data-testid="res-category">
                {categories.map((c) => <option key={c.key} value={c.key}>{c.label.fr}</option>)}
              </select>
            </Field>
            <div className="space-y-1.5">
              <span className="text-xs font-semibold" style={MUTED}>Image de couverture (16:9)</span>
              {art.cover_url ? <img src={resolveMediaUrl(art.cover_url)} alt="" className="w-full aspect-[16/9] object-cover rounded-lg" data-testid="res-cover-preview" />
                : <div className="w-full aspect-[16/9] rounded-lg grid place-items-center" style={{ background: "var(--yalola-surface-alt)", ...MUTED }} data-testid="res-cover-empty"><ImageIcon size={22} /></div>}
              <div className="flex items-center gap-2">
                <ImageCropUpload aspect={16 / 9} cropShape="rect" onUploaded={(u) => setField("cover_url", u)} buttonLabel={art.cover_url ? "Remplacer" : "Ajouter"} testId="res-cover-upload" title="Ajuster la couverture" />
                <Button size="sm" variant="outline" onClick={aiCover} disabled={!!busy || !frTitle.trim()} title="Illustration générée par l'IA aux couleurs Yalola" data-testid="res-cover-ai">{busy === "cover" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}IA</Button>
                {art.cover_url && <Button size="sm" variant="ghost" onClick={() => setField("cover_url", "")} data-testid="res-cover-remove">Retirer</Button>}
              </div>
            </div>
            <Field label="Adresse (slug)" hint="Généré depuis le titre FR si vide.">
              <Input value={art.slug || ""} onChange={(e) => setField("slug", e.target.value)} placeholder="mon-article" data-testid="res-slug" />
            </Field>
            <Field label="Auteur affiché">
              <Input value={art.author_label || ""} onChange={(e) => setField("author_label", e.target.value)} maxLength={120} data-testid="res-author" />
            </Field>
          </div>

          <div className="yalola-card p-4 space-y-3">
            <div className="text-sm font-bold flex items-center gap-2"><Languages size={16} />Traduction IA</div>
            <p className="text-xs" style={MUTED}>Traduit le titre, l'extrait, le corps et la méta-description du français vers EN, ES et PT (langues encore vides). À relire avant publication.</p>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="res-translate-overwrite" />Écraser les traductions existantes</label>
            <Button size="sm" variant="outline" className="w-full" onClick={translate} disabled={!!busy || !frTitle.trim()} data-testid="res-translate">
              {busy === "translate" ? <Loader2 size={15} className="animate-spin" /> : <Languages size={15} />}Traduire EN · ES · PT
            </Button>
            {art.id && (
              <a href={`/ressources/${art.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-black/5" style={{ borderColor: "var(--yalola-border)" }} data-testid="res-preview"><Eye size={15} />Aperçu {art.status === "published" ? "" : "(brouillon, admin seulement)"}</a>
            )}
            {art.id && !confirmDel && <Button size="sm" variant="ghost" className="w-full" style={{ color: "#B91C1C" }} onClick={() => setConfirmDel(true)} data-testid="res-delete"><Trash2 size={15} />Supprimer l'article</Button>}
            {confirmDel && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="destructive" className="flex-1" onClick={remove} data-testid="res-delete-confirm">Confirmer la suppression</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDel(false)} data-testid="res-delete-cancel">Annuler</Button>
              </div>
            )}
          </div>
          {art.id && <ResourceHistory history={art.history} />}
        </aside>
      </div>
    </div>
  );
};
